import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { APP_ID_NAME_MAP } from '@autobid/ui/constants/APP_ID_NAME_MAP'
import { APPS_THAT_NEED_AUTH } from '@autobid/ui/constants/APPS_THAT_NEED_AUTH'
import { watch } from 'vue'
import { useCheckUserPermission } from './useCheckUserPermission'
import { usePdfQuery } from './usePdfQuery'

export function useAccessToApp() {
  const { shouldGenerate } = usePdfQuery()
  if (shouldGenerate.value) {
    return { hasAccess: ref(true) }
  }
  const APP_ID = useRuntimeConfig().public.APP_ID as number
  const { t } = useI18n()
  const hasAccess = ref(false)
  const { isAuthed, setPageRedirectData } = useAutobidAuth()
  const { checkUserPermission } = useCheckUserPermission()

  const checkAccessToApp = (withoutProfile?: boolean) => {
    const doesAppNeedAuth = APPS_THAT_NEED_AUTH.includes(
      APP_ID_NAME_MAP[APP_ID as keyof typeof APP_ID_NAME_MAP],
    )
    if (!doesAppNeedAuth)
      return 200

    const result = withoutProfile ? false : checkUserPermission('login', APP_ID)

    if (!result) {
      return withoutProfile || !isAuthed.value ? 401 : 403
    }

    return 200
  }

  const updatePageRedirectData = () => {
    if (!isAuthed.value) {
      setPageRedirectData({ redirectable: true })
      return
    }

    const status = checkAccessToApp(true)

    setPageRedirectData({ redirectable: status === 200 })
  }

  const validateAccess = () => {
    const status = checkAccessToApp()

    updatePageRedirectData()

    if (status === 200) {
      hasAccess.value = true
    }
    else {
      hasAccess.value = false

      showError({
        statusCode: status,
        statusMessage:
          status === 403
            ? t('error-page.403.description')
            : t('error-page.401.description'),
      })
    }
  }

  validateAccess()

  watch(isAuthed, () => {
    validateAccess()
  })

  return { hasAccess }
}
